import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, Tag, Drawer, Form, Input, Tree, Modal, Tabs, Checkbox, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getRoleList, saveRole, deleteRole } from '../../api/system';
import { hasPerm } from '../../hooks/usePermission';
import MENU_RESOURCE from '../../constants/menuResource';
import FIELD_RESOURCE from '../../constants/fieldResource';

// 菜单资源 -> antd Tree 数据（一级分类不可勾选，仅作分组）
const buildTreeData = () => MENU_RESOURCE.map((group) => ({
    key: group.code,
    title: group.label,
    checkable: false,
    selectable: false,
    children: group.children.map((menu) => {
        const btnChildren = (menu.buttons || []).map((b) => ({ key: b.code, title: b.label }));
        return {
            key: menu.code,
            title: menu.label,
            children: btnChildren.length ? btnChildren : undefined,
        };
    }),
}));

// 角色 codes（含菜单+按钮的全集）-> Tree 的 { checked, halfChecked }
const toCheckedState = (codes) => {
    const set = new Set(codes || []);
    const checked = [];
    const halfChecked = [];
    MENU_RESOURCE.forEach((g) => g.children.forEach((m) => {
        const btns = (m.buttons || []).map((b) => b.code);
        if (btns.length) {
            const hit = btns.filter((c) => set.has(c));
            if (hit.length === btns.length && hit.length > 0) {
                checked.push(m.code);
            } else if (hit.length > 0) {
                halfChecked.push(m.code);
            }
            hit.forEach((c) => checked.push(c));
        } else if (set.has(m.code)) {
            checked.push(m.code);
        }
    }));
    return { checked, halfChecked };
};

const RoleManage = () => {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [codes, setCodes] = useState([]);
    const [hiddenFields, setHiddenFields] = useState({});
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const treeData = useMemo(() => buildTreeData(), []);
    const checkedKeys = useMemo(() => toCheckedState(codes), [codes]);

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await getRoleList();
            if (res.code === 0) {
                setDataSource(res.data.list);
            } else {
                message.error(res.message || '获取角色列表失败');
            }
        } catch (error) {
            console.error('Fetch role list failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const openAdd = () => {
        setEditing(null);
        setCodes([]);
        setHiddenFields({});
        form.resetFields();
        setDrawerOpen(true);
    };

    const openEdit = (record) => {
        if (record.is_builtin) {
            message.info('内置角色不可编辑');
            return;
        }
        setEditing(record);
        form.setFieldsValue({ name: record.name, desc: record.desc });
        setCodes(record.codes || []);
        setHiddenFields(record.hidden_fields || {});
        setDrawerOpen(true);
    };

    const toggleField = (listCode, fieldCode, checked) => {
        setHiddenFields((prev) => {
            const cur = new Set(prev[listCode] || []);
            if (checked) cur.delete(fieldCode); else cur.add(fieldCode);
            return { ...prev, [listCode]: Array.from(cur) };
        });
    };

    const toggleAllFields = (listCode, checked) => {
        setHiddenFields((prev) => {
            const list = FIELD_RESOURCE.find((l) => l.code === listCode);
            const optionalCodes = (list?.fields || []).filter((f) => !f.required).map((f) => f.code);
            const cur = new Set(prev[listCode] || []);
            optionalCodes.forEach((code) => {
                if (checked) cur.delete(code); else cur.add(code);
            });
            return { ...prev, [listCode]: Array.from(cur) };
        });
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        setSaving(true);
        try {
            const res = await saveRole({
                id: editing?.id,
                name: values.name,
                desc: values.desc,
                codes,
                hidden_fields: hiddenFields,
            });
            if (res.code === 0) {
                message.success('保存成功');
                setDrawerOpen(false);
                fetchList();
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            if (error.errorFields) return;
            console.error('Save role failed:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '删除确认',
            content: `确定删除角色「${record.name}」吗？`,
            okText: '确认删除',
            okButtonProps: { danger: true },
            cancelText: '取消',
            centered: true,
            onOk: async () => {
                const res = await deleteRole({ id: record.id });
                if (res.code === 0) {
                    message.success('删除成功');
                    fetchList();
                } else {
                    message.error(res.message || '删除失败');
                }
            },
        });
    };

    const columns = [
        { title: '角色名称', dataIndex: 'name', key: 'name', width: 140 },
        { title: '角色编码', dataIndex: 'code', key: 'code', width: 140 },
        { title: '描述', dataIndex: 'desc', key: 'desc', render: (v) => v || '-' },
        {
            title: '权限', dataIndex: 'codes', key: 'codes', width: 130,
            render: (v, record) => record.is_builtin ? <Tag color="geekblue">全部权限</Tag> : <span>{(v || []).length} 项</span>,
        },
        { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
        {
            title: '操作', key: 'action', width: 120,
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, record) => (
                <Space>
                    {hasPerm('system:role:edit') && (
                        <Button type="link" disabled={record.is_builtin} onClick={() => openEdit(record)}>编辑</Button>
                    )}
                    {hasPerm('system:role:delete') && (
                        <Button type="link" danger disabled={record.is_builtin} onClick={() => handleDelete(record)}>删除</Button>
                    )}
                </Space>
            ),
        },
    ];

    const fieldConfigContent = (
        <div>
            <div style={{ color: '#999', fontSize: 12, marginBottom: 12 }}>
                默认全选，取消勾选即该角色不可查看该字段；标注「必显」的字段不可取消。
            </div>
            {FIELD_RESOURCE.map((list) => {
                const hiddenSet = new Set(hiddenFields[list.code] || []);
                const optionalFields = list.fields.filter((f) => !f.required);
                const selectedCount = list.fields.filter((f) => !hiddenSet.has(f.code)).length;
                const allSelected = optionalFields.every((f) => !hiddenSet.has(f.code));
                const someSelected = optionalFields.some((f) => !hiddenSet.has(f.code));
                return (
                    <div key={list.code} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontWeight: 500 }}>{list.label}</span>
                            <Space size={12}>
                                <span style={{ color: '#999', fontSize: 12 }}>已选 {selectedCount}/{list.fields.length}</span>
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    onChange={(e) => toggleAllFields(list.code, e.target.checked)}
                                >
                                    全选
                                </Checkbox>
                            </Space>
                        </div>
                        <Space wrap>
                            {list.fields.map((f) => (
                                <Checkbox
                                    key={f.code}
                                    checked={!hiddenSet.has(f.code)}
                                    disabled={f.required}
                                    onChange={(e) => toggleField(list.code, f.code, e.target.checked)}
                                >
                                    {f.label}{f.required ? '（必显）' : ''}
                                </Checkbox>
                            ))}
                        </Space>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: 16 }}>
                {hasPerm('system:role:add') && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增角色</Button>
                )}
            </div>

            <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} pagination={false} />

            <Drawer
                title={editing ? '编辑角色' : '新增角色'}
                width={560}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
                            <Button type="primary" loading={saving} onClick={handleSave}>保存</Button>
                        </Space>
                    </div>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}>
                        <Input placeholder="请输入角色名称" maxLength={30} />
                    </Form.Item>
                    <Form.Item name="desc" label="描述">
                        <Input placeholder="请输入描述" maxLength={100} />
                    </Form.Item>
                </Form>

                <Tabs
                    items={[
                        {
                            key: 'menu',
                            label: '菜单权限',
                            children: (
                                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8, maxHeight: 380, overflow: 'auto' }}>
                                    <Tree
                                        checkable
                                        defaultExpandAll
                                        selectable={false}
                                        treeData={treeData}
                                        checkedKeys={checkedKeys}
                                        onCheck={(checked, info) => setCodes([...checked, ...(info.halfCheckedKeys || [])])}
                                    />
                                </div>
                            ),
                        },
                        {
                            key: 'field',
                            label: '字段权限',
                            children: (
                                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12, maxHeight: 380, overflow: 'auto' }}>
                                    {fieldConfigContent}
                                </div>
                            ),
                        },
                    ]}
                />
            </Drawer>
        </div>
    );
};

export default RoleManage;
